import axios from 'axios';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchIcon from '@mui/icons-material/Search';
import { useSession, xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import { encodeSearchWord, timestamp2Localestring } from '../../common/helper';
import { KEY_BOOK_GROUP_LIST, KEY_BOOK_INFO_LIST } from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  deleteBookGroup,
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';

const columns = [
  '제목',
  '대상 학군',
  '대상 학년',
  '대상 학교',
  '대상 학급',
  '추천 도서권수',
  '도서 상세보기',
  '도서 엑셀파일',
];

interface Props {}

export const BookInquire: React.FC<Props> = () => {
  const queryClient = useQueryClient();
  const formInputRef = useRef<any>();
  const { loginVal } = useSession();
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [resultFilter, setResultFilter] = useState<string>('');
  const [requestList, setRequestList] = useState<any>([]);
  const [bookGroupList, setBookGroupList] = useState<any>([]);
  const [bookInfoList, setBookInfoList] = useState<any>([]);
  const [isAdd, setIsAdd] = useState<boolean>(true);
  const [bookGroupSeq, setBookGroupSeq] = useState<number>(-1);
  const [selectedRow, setSelectedRow] = useState<number>(-1);
  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState<string>('book-group');
  const [searchWord, setSearchWord] = useState<string>('');
  const [dailyStatType, setDailyStatType] = useState<any>(0);
  const [loading, setLoading] = useState<any>(false);
  const [downloadType, setDownloadType] = useState<any>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openSchoolClass, setOpenSchoolClass] = useState<boolean>(false);
  const [openBookList, setOpenBookList] = useState<boolean>(false);
  const [hoveredRow, setHoveredRow] = useState<number>(-1);

  const {
    data,
    isLoading,
    refetch: bookGroupRefetch,
  } = useQuery(
    KEY_BOOK_GROUP_LIST,
    () => {
      const newData = {
        page: page,
        rowsPerPage: rowsPerPage,
        searchKey: 'book-group',
        searchWord: encodeSearchWord(searchKey, searchWord),
      };
      if (reqRequest) return getBookGroupList(newData);
      return null;
    },
    {
      onSuccess: res => {
        if (res) {
          const newSchoolClassInfo =
            res.bookGroupList &&
            res.bookGroupList.map((item: any, idx: number) => {
              const newData = [
                item.bookGroupName,
                item.schoolGroupName !== null ? item.schoolGroupName : '-',
                item.schoolGrade !== null ? item.schoolGrade : '-',
                item.schoolName,
                item.schoolGrade !== null && item.schoolClass !== null
                  ? `${item.schoolGrade}학년 ${item.schoolClass}반`
                  : '-',
                item.bookCount,
                '-',
                '-',
              ];
              return newData;
            });
          setRequestList(newSchoolClassInfo);
          setBookGroupList(res.bookGroupList);
          setTotalRecords(res.totalRecordCount);
        }
        setLoading(false);
      },
      onError: e => {
        console.log(e);
        setLoading(false);
      },
    }
  );

  const bookGroupPrefetch = (newPage: number) => {
    queryClient.prefetchQuery(KEY_BOOK_GROUP_LIST, () => {
      const newData = {
        page: page,
        rowsPerPage: rowsPerPage,
        searchKey: 'book-group',
        searchWord: encodeSearchWord(searchKey, searchWord),
      };
      getBookGroupList(newData);
    });
  };

  const { refetch: bookInfoRefetch } = useQuery(
    KEY_BOOK_INFO_LIST,
    () => {
      const newData = {
        bookGroupSeq: bookGroupSeq,
        page: 0,
        rowsPerPage: 0,
        searchKey: 'book-info',
        searchWord: encodeSearchWord(searchKey, searchWord),
      };
      if (reqRequest) return getBookInfoList(newData);
      return null;
    },
    {
      onSuccess: res => {
        if (res) {
          setBookInfoList(res.bookInfoList);
          setOpenBookList(true);
        }
        setLoading(false);
        setBookGroupSeq(-1);
      },
      onError: e => {
        console.log(e);
        setLoading(false);
        setBookGroupSeq(-1);
      },
    }
  );

  const downloadBookGroupList = (param: any) =>
    xapi
      .get(
        `/admin/v1/apis/book/download/book-group?searchKey=${param.searchKey}&searchWord=${param.searchWord}&startDate=${param.startDate}&endDate=${param.endDate}`,
        { responseType: 'blob' }
      )
      .then(res => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `book_group.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout((_: any) => {
          window.URL.revokeObjectURL(url);
        }, 600000);
        a.remove();
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.error('err: ', err);
      });

  const downloadBookGroup = () => {
    const newData = {
      searchKey: 'book-group',
      searchWord: resultFilter,
      startDate: 0,
      endDate: 0,
    };
    setLoading(true);
    downloadBookGroupList(newData);
  };

  const downloadBookGroupBookInfoList = (param: any) =>
    xapi
      .get(
        `/admin/v1/apis/book/download/book-group/${param.bookGroupSeq}/book-info?searchKey=${param.searchKey}&searchWord=${param.searchWord}&startDate=${param.startDate}&endDate=${param.endDate}`,
        { responseType: 'blob' }
      )
      .then(res => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `book_info.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout((_: any) => {
          window.URL.revokeObjectURL(url);
        }, 600000);
        a.remove();
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.error('err: ', err);
      });

  const downloadBookInfo = (idx: number) => {
    const newData = {
      bookGroupSeq: bookGroupList[idx].bookGroupSeq,
      searchKey: 'book-group',
      searchWord: resultFilter,
      startDate: 0,
      endDate: 0,
    };
    setLoading(true);
    downloadBookGroupBookInfoList(newData);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const searchBookGroup = () => {
    if (page === 1) {
      setLoading(true);
      bookGroupRefetch();
    } else {
      setPage(1);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      searchBookGroup();
    }
  };

  const downloadSampleExcel = () => {
    const url =
      'https://d15wsbb3qf2hxa.cloudfront.net/common/%ED%95%99%EA%B5%90_%EC%97%91%EC%85%80%EC%97%85%EB%A1%9C%EB%93%9C%EC%9A%A9_%EC%96%91%EC%8B%9D.xlsx';
    const a = document.createElement('a');
    a.href = url;
    a.download = `업로드양식.xlsx`;
    document.body.appendChild(a);
    a.click();
    setTimeout((_: any) => {
      window.URL.revokeObjectURL(url);
    }, 60000);
    a.remove();
  };

  const { mutate: registerBookGroupMutate, isError: isMutateError } =
    useMutation(registerBookGroup, {
      onSuccess: res => {
        if (res.resultCode === RESULT_OK) {
          setOpenSnackbar(true);
          setDialogMessage('추천도서를 등록했습니다.');
          bookGroupRefetch();
        } else {
          setOpenSnackbar(true);
          setDialogMessage(
            `추천도서를 등록하는 동안 오류가 발생했습니다.(${res.resultMessage})`
          );
        }
        setLoading(false);
      },
      onError: error => {
        setOpenSnackbar(true);
        setDialogMessage(
          '추천도서 저장이 실패했습니다. 다시 시도해주시기 바랍니다.'
        );
        setLoading(false);
      },
    });

  const saveBookGroupInfo = async (item: any) => {
    var newUploadUrl = '';
    if (item.uploadFile !== undefined && item.uploadFile.file !== null) {
      const newUploadFile: any = await uploadFile('book', item.uploadFile.file);
      newUploadUrl = newUploadFile.data.result.file.path;
    }

    const newData: any = {
      tabValue: item.tabValue,
      bookGroupSeq: item.bookGroupSeq,
      bookGroupName: item.bookGroupName,
      description: item.description,
      schoolClassSeq: item.schoolClassSeq,
      educationOfficeSeq: item.educationOfficeSeq,
      schoolSeq: item.schoolSeq,
      schoolGroupSeq: item.schoolGroupSeq,
      schoolGrade: item.schoolGrade,
      schoolClass: item.schoolClass,
      imageUrl: item.imageUrl,
      uploadUrl: newUploadUrl,
    };
    setLoading(true);
    registerBookGroupMutate(newData);
  };

  const { mutate: deleteBookGroupMutate, isError: isDeleteMutateError } =
    useMutation(deleteBookGroup, {
      onSuccess: res => {
        if (res.resultCode === RESULT_OK) {
          setOpenSnackbar(true);
          setDialogMessage('추천도서를 삭제했습니다.');
          bookGroupRefetch();
        }
      },
      onError: error => {
        setOpenSnackbar(true);
        setDialogMessage('추천도서를 삭제하는 동안 오류가 발생했습니다.');
      },
    });

  const deleteBookGroupInfo = (item: any) => {
    deleteBookGroupMutate(item);
  };

  const detailBookGroup = (idx: number) => {
    const newSeq = bookGroupList[idx].bookGroupSeq;
    if (newSeq !== null) setBookGroupSeq(newSeq);
  };

  const clickRow = (idx: number, opType: boolean) => {
    setSelectedRow(idx);
    setIsAdd(opType);
    setOpenSchoolClass(true);
  };

  useEffect(() => {
    setLoading(true);
    bookGroupRefetch();
  }, [page]);

  useEffect(() => {
    if (bookGroupSeq > 0) {
      bookInfoRefetch();
    }
  }, [bookGroupSeq]);

  useEffect(() => {
    if (reqRequest) {
      bookGroupRefetch();
    }
  }, [reqRequest]);

  useEffect(() => {
    setTimeout((_: any) => {
      setReqRequest(true);
    }, 500);
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <Box className='flex_between' sx={{ p: '1rem 0' }}>
          <TextField
            sx={{ width: '24rem' }}
            placeholder='추천 도서 목록으로 검색'
            value={searchWord}
            onChange={evt => setSearchWord(evt.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton sx={{ pr: 0 }} onClick={searchBookGroup}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className='flex_between'>
          <Box>
            <Button
              color='info'
              variant='outlined'
              onClick={() => {
                clickRow(-1, true);
              }}
            >
              추가
            </Button>
          </Box>
          <Box>
            <Button
              sx={{ mr: '1rem' }}
              color='info'
              variant='outlined'
              onClick={() => {
                downloadBookGroup();
              }}
            >
              다운로드
              <FileDownloadRoundedIcon color='success' />
            </Button>
          </Box>
        </Box>
        <Paper sx={{ p: '1rem' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column: any, idx: number) => (
                    <TableCell key={idx.toString()} sx={{ p: '.4rem .7rem' }}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {requestList.length > 0 &&
                  requestList.map((row: any, idx: number) => (
                    <TableRow
                      key={idx.toString()}
                      style={{
                        cursor: hoveredRow === idx ? 'pointer' : 'default',
                      }}
                      onMouseEnter={() => setHoveredRow(idx)}
                      onMouseLeave={() => setHoveredRow(-1)}
                    >
                      {columns.map((column: any, i: number) =>
                        i === columns.length - 2 ? (
                          <TableCell key={i.toString()} sx={{ p: '.7rem' }}>
                            <Button
                              variant='outlined'
                              color='info'
                              onClick={() => detailBookGroup(idx)}
                            >
                              보기
                            </Button>
                          </TableCell>
                        ) : i === columns.length - 1 ? (
                          <TableCell key={i.toString()} sx={{ p: '.7rem' }}>
                            <Button
                              variant='outlined'
                              color='info'
                              onClick={() => {
                                downloadBookInfo(idx);
                              }}
                            >
                              다운로드
                              <FileDownloadRoundedIcon color='info' />
                            </Button>
                          </TableCell>
                        ) : (
                          <TableCell
                            key={i.toString()}
                            sx={{ p: '.7rem' }}
                            onClick={() => clickRow(idx, false)}
                          >
                            {row[i]}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <SwPagination
            page={page}
            handleChangePage={handleChangePage}
            count={Math.ceil(totalRecords / rowsPerPage)}
          />
        </Paper>
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      <SwBookGroup
        open={openSchoolClass}
        onClose={() => setOpenSchoolClass(false)}
        isAdd={isAdd}
        bookGroupInfo={bookGroupList[selectedRow]}
        onSave={(item: any) => {
          saveBookGroupInfo(item);
        }}
        onDelete={(item: any) => {
          deleteBookGroupInfo(item);
        }}
      />
      <SwBookList
        open={openBookList}
        onClose={() => setOpenBookList(false)}
        bookList={bookInfoList}
      />
      {loading && <SwLoading />}
    </>
  );
};
